import { z } from "zod"

import {
  platformCommandEnvelopeV1Schema,
} from "./platform-command-v1.js"
import { tenantIdSchema } from "./identifiers.js"

const codeSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_.-]{1,63}$/)

export const platformEntitlementValueV1Schema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("access"),
      enabled: z.boolean(),
    }).strict(),
    z.object({
      kind: z.literal("limit"),
      value: z.number().int().nonnegative(),
      unit: z.enum(["count", "bytes", "days"]),
    }).strict(),
  ],
)

export const platformEntitlementGrantV1Schema = z.object({
  capability: codeSchema,
  value: platformEntitlementValueV1Schema,
}).strict()

const catalogRevisionSchema = z.object({
  code: codeSchema,
  revision: z.number().int().positive(),
  grants: z.array(platformEntitlementGrantV1Schema).max(128),
}).strict().superRefine((revision, context) => {
  const capabilities = new Set<string>()
  for (const [index, grant] of revision.grants.entries()) {
    if (capabilities.has(grant.capability)) {
      context.addIssue({
        code: "custom",
        path: ["grants", index, "capability"],
        message: "A catalog revision cannot grant one capability twice.",
      })
    }
    capabilities.add(grant.capability)
  }
})

export const platformPlanRevisionV1Schema = catalogRevisionSchema
export const platformAddOnRevisionV1Schema = catalogRevisionSchema

export const platformEntitlementOverrideV1Schema = z.object({
  capability: codeSchema,
  value: platformEntitlementValueV1Schema,
  reasonCode: z.enum([
    "contract",
    "trial",
    "promotion",
    "service_recovery",
  ]),
}).strict()

const tenantEntitlementPolicyBaseV1Schema = z.object({
  contractVersion: z.literal(1),
  targetTenantId: tenantIdSchema,
  version: z.number().int().positive(),
  plan: platformPlanRevisionV1Schema,
  addOns: z.array(platformAddOnRevisionV1Schema).max(32),
  overrides: z.array(platformEntitlementOverrideV1Schema).max(64),
  effectiveFrom: z.iso.datetime({ offset: true }),
  effectiveUntil: z.iso.datetime({ offset: true }).nullable(),
  createdAt: z.iso.datetime({ offset: true }),
}).strict()

const validatePolicy = (
  policy: {
    effectiveFrom: string
    effectiveUntil: string | null
    addOns: ReadonlyArray<{ code: string; revision: number }>
    overrides: ReadonlyArray<{ capability: string }>
  },
  context: z.RefinementCtx,
) => {
  if (
    policy.effectiveUntil !== null
    && Date.parse(policy.effectiveUntil) <= Date.parse(policy.effectiveFrom)
  ) {
    context.addIssue({
      code: "custom",
      path: ["effectiveUntil"],
      message: "An Entitlement policy must end after it starts.",
    })
  }
  const addOns = new Set<string>()
  for (const [index, addOn] of policy.addOns.entries()) {
    const key = `${addOn.code}:${addOn.revision}`
    if (addOns.has(key)) {
      context.addIssue({
        code: "custom",
        path: ["addOns", index],
        message: "An add-on revision cannot be assigned twice.",
      })
    }
    addOns.add(key)
  }
  const overrides = new Set<string>()
  for (const [index, override] of policy.overrides.entries()) {
    if (overrides.has(override.capability)) {
      context.addIssue({
        code: "custom",
        path: ["overrides", index, "capability"],
        message: "A capability cannot be overridden twice.",
      })
    }
    overrides.add(override.capability)
  }
}

export const tenantEntitlementPolicyV1Schema =
  tenantEntitlementPolicyBaseV1Schema.superRefine(validatePolicy)

const tenantEntitlementPolicyInputV1Schema =
  tenantEntitlementPolicyBaseV1Schema
    .omit({ version: true, createdAt: true })
    .superRefine(validatePolicy)

export const setTenantEntitlementPolicyV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema,
  policy: tenantEntitlementPolicyInputV1Schema,
}).strict()

export const effectivePlatformEntitlementV1Schema = z.object({
  capability: codeSchema,
  value: platformEntitlementValueV1Schema,
  source: z.enum(["plan", "add_on", "override"]),
  sourceCode: codeSchema,
  sourceRevision: z.number().int().positive().nullable(),
}).strict()

export const tenantEntitlementSnapshotV1Schema = z.object({
  responseVersion: z.literal(1),
  targetTenantId: tenantIdSchema,
  evaluatedAt: z.iso.datetime({ offset: true }),
  policyVersion: z.number().int().positive().nullable(),
  state: z.enum(["active", "absent", "not_yet_effective", "expired"]),
  entitlements: z.array(effectivePlatformEntitlementV1Schema).max(256),
  preservesBusinessState: z.literal(true),
}).strict()

export const tenantCapabilityDecisionV1Schema = z.object({
  responseVersion: z.literal(1),
  targetTenantId: tenantIdSchema,
  capability: codeSchema,
  evaluatedAt: z.iso.datetime({ offset: true }),
  allowed: z.boolean(),
  reason: z.enum([
    "entitled",
    "absent_entitlement",
    "not_yet_effective",
    "expired_entitlement",
    "explicitly_disabled",
    "limit_exhausted",
  ]),
  policyVersion: z.number().int().positive().nullable(),
  limit: z.object({
    value: z.number().int().nonnegative(),
    unit: z.enum(["count", "bytes", "days"]),
  }).strict().nullable(),
  preservesBusinessState: z.literal(true),
}).strict()

export type PlatformEntitlementGrantV1 = z.infer<
  typeof platformEntitlementGrantV1Schema
>
export type PlatformEntitlementOverrideV1 = z.infer<
  typeof platformEntitlementOverrideV1Schema
>
export type TenantEntitlementPolicyV1 = z.infer<
  typeof tenantEntitlementPolicyV1Schema
>
export type SetTenantEntitlementPolicyV1 = z.infer<
  typeof setTenantEntitlementPolicyV1Schema
>
export type TenantEntitlementSnapshotV1 = z.infer<
  typeof tenantEntitlementSnapshotV1Schema
>
export type TenantCapabilityDecisionV1 = z.infer<
  typeof tenantCapabilityDecisionV1Schema
>
